import { useState, useEffect, useCallback, useMemo } from 'react'
import { Navigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Switch,
  Checkbox,
  Chip,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import PolicyIcon from '@mui/icons-material/Policy'
import { salesPolicyService } from '../services'
import useAuthStore from '../store/authStore'

const SalesPolicyPage = () => {
  const isSuperuserActor = useAuthStore((s) => s.isSuperuserActor)
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [search, setSearch] = useState('')
  const [error, setError] = useState(null)
  const [snackbar, setSnackbar] = useState(null)
  const [selected, setSelected] = useState(() => new Set())
  const [togglingOrgId, setTogglingOrgId] = useState(null)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [confirmBulk, setConfirmBulk] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await salesPolicyService.list()
      const list = Array.isArray(data) ? data : []
      setRows(list)
      setError(null)
    } catch (e) {
      setError(
        e.response?.data?.message ||
          e.message ||
          'Failed to load sales policies',
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isSuperuserActor) return
    load()
  }, [load, isSuperuserActor])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => {
      const name = String(r.organization_name || '').toLowerCase()
      const id = String(r.org_id ?? '')
      return name.includes(q) || id.includes(q)
    })
  }, [rows, search])

  const enabledCount = useMemo(
    () => rows.filter((r) => r.allow_negative_sales).length,
    [rows],
  )

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((r) => selected.has(r.org_id))
  const someFilteredSelected =
    filtered.some((r) => selected.has(r.org_id)) && !allFilteredSelected

  const toggleSelectAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allFilteredSelected) {
        filtered.forEach((r) => next.delete(r.org_id))
      } else {
        filtered.forEach((r) => next.add(r.org_id))
      }
      return next
    })
  }

  const toggleSelectRow = (orgId) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(orgId)) next.delete(orgId)
      else next.add(orgId)
      return next
    })
  }

  const handleToggle = async (row, allow) => {
    setTogglingOrgId(row.org_id)
    try {
      await salesPolicyService.update(row.org_id, allow)
      setRows((prev) =>
        prev.map((r) =>
          r.org_id === row.org_id
            ? { ...r, allow_negative_sales: allow }
            : r,
        ),
      )
      setSnackbar({
        severity: 'success',
        message: `Negative sales ${allow ? 'enabled' : 'disabled'} for ${row.organization_name || `org ${row.org_id}`}`,
      })
    } catch (e) {
      setSnackbar({
        severity: 'error',
        message:
          e.response?.data?.message ||
          e.message ||
          'Failed to update sales policy',
      })
    } finally {
      setTogglingOrgId(null)
    }
  }

  const runBulk = async () => {
    if (!confirmBulk) return
    setBulkBusy(true)
    try {
      const { mode, org_ids, allow_negative_sales } = confirmBulk
      await salesPolicyService.bulk({
        mode,
        org_ids,
        allow_negative_sales,
      })
      await load()
      if (mode === 'selected') setSelected(new Set())
      setSnackbar({
        severity: 'success',
        message:
          mode === 'all_on'
            ? 'Negative sales enabled for all outlets'
            : mode === 'all_off'
              ? 'Negative sales disabled for all outlets'
              : `Negative sales ${allow_negative_sales ? 'enabled' : 'disabled'} for ${org_ids?.length || 0} selected outlet(s)`,
      })
      setConfirmBulk(null)
    } catch (e) {
      setSnackbar({
        severity: 'error',
        message:
          e.response?.data?.message ||
          e.message ||
          'Bulk update failed',
      })
    } finally {
      setBulkBusy(false)
    }
  }

  const confirmLabel = () => {
    if (!confirmBulk) return ''
    if (confirmBulk.mode === 'all_on') {
      return 'Enable negative sales for all outlets?'
    }
    if (confirmBulk.mode === 'all_off') {
      return 'Disable negative sales for all outlets?'
    }
    return `Enable negative sales for ${confirmBulk.org_ids?.length || 0} selected outlet(s)?`
  }

  if (!isSuperuserActor) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <PolicyIcon sx={{ color: '#e90000', fontSize: 32 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Sales Policy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Control which outlets may sell without inventory (negative
            sales).
          </Typography>
        </Box>
      </Box>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Chip
          label={`${enabledCount} enabled`}
          color="success"
          size="small"
          variant="outlined"
        />
        <Chip
          label={`${rows.length - enabledCount} disabled`}
          size="small"
          variant="outlined"
        />
        <Chip label={`${rows.length} outlets`} size="small" />
      </Stack>

      <Card>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ mb: 2 }}
            alignItems={{ sm: 'center' }}
          >
            <TextField
              size="small"
              placeholder="Search shop name or ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 280 }}
              InputProps={{
                endAdornment: search ? (
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setSearch('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : null,
              }}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="outlined"
              size="small"
              disabled={bulkBusy || loading || selected.size === 0}
              onClick={() =>
                setConfirmBulk({
                  mode: 'selected',
                  org_ids: Array.from(selected),
                  allow_negative_sales: true,
                })
              }
            >
              Enable selected ({selected.size})
            </Button>
            <Button
              variant="contained"
              size="small"
              color="success"
              disabled={bulkBusy || loading || rows.length === 0}
              onClick={() => setConfirmBulk({ mode: 'all_on' })}
            >
              Enable all
            </Button>
            <Button
              variant="contained"
              size="small"
              color="inherit"
              disabled={bulkBusy || loading || rows.length === 0}
              onClick={() => setConfirmBulk({ mode: 'all_off' })}
            >
              Disable all
            </Button>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={someFilteredSelected}
                        checked={allFilteredSelected}
                        onChange={toggleSelectAllFiltered}
                        disabled={filtered.length === 0}
                        inputProps={{ 'aria-label': 'Select all filtered' }}
                      />
                    </TableCell>
                    <TableCell>Shop ID</TableCell>
                    <TableCell>Shop Name</TableCell>
                    <TableCell align="right">Negative Sales</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No outlets found.
                      </TableCell>
                    </TableRow>
                  )}
                  {filtered.map((row) => (
                    <TableRow key={row.org_id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.has(row.org_id)}
                          onChange={() => toggleSelectRow(row.org_id)}
                        />
                      </TableCell>
                      <TableCell>{row.org_id}</TableCell>
                      <TableCell>{row.organization_name || '—'}</TableCell>
                      <TableCell align="right">
                        <Switch
                          checked={Boolean(row.allow_negative_sales)}
                          disabled={togglingOrgId === row.org_id || bulkBusy}
                          onChange={(e) =>
                            handleToggle(row, e.target.checked)
                          }
                          inputProps={{
                            'aria-label': `Toggle negative sales for ${row.organization_name || row.org_id}`,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(confirmBulk)}
        onClose={() => !bulkBusy && setConfirmBulk(null)}
      >
        <DialogTitle>Confirm bulk update</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmLabel()}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulk(null)} disabled={bulkBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={runBulk}
            disabled={bulkBusy}
          >
            {bulkBusy ? <CircularProgress size={22} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(null)}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  )
}

export default SalesPolicyPage
